import React, { useState, useRef, useEffect } from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  ArrowRight,
  CheckCircle,
  Phone,
  Mail,
  User,
  Loader2,
  Upload,
  FileText,
  Sparkles,
  TrendingDown,
  Award,
  HeartHandshake,
  Lock,
  Info,
  Star,
  Zap,
  Clock,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Marketing data ----
const promises = [
  {
    icon: TrendingDown,
    title: 'Same Products. Better Value.',
    desc: 'Send us any written offer for solar, battery, heat pump or hot water from a verified Australian installer. Our team reviews every submission and comes back with our best offer.*',
  },
  {
    icon: Sparkles,
    title: 'Comparable If Not Identical.',
    desc: 'If we cannot beat the price on the exact product or brand you have been quoted, we will offer a comparable Tier-1 alternative that delivers better overall value.*',
  },
  {
    icon: HeartHandshake,
    title: 'Superior Service. Always.',
    desc: 'Direct after-sales line, CEC-accredited installers, real humans, real warranty support. Not a faceless call centre.',
  },
];

const howItWorks = [
  {
    n: '01',
    icon: User,
    title: 'Tell us about you',
    desc: 'Quick name, email & mobile — takes 20 seconds.',
  },
  {
    n: '02',
    icon: Lock,
    title: 'Verify by OTP',
    desc: "We send a 6-digit code to your mobile so we know it's really you.",
  },
  {
    n: '03',
    icon: Upload,
    title: 'Upload the offer',
    desc: 'Drop in the PDF, photo or screenshot of the deal you have in hand.',
  },
  {
    n: '04',
    icon: Sparkles,
    title: 'We review & respond',
    desc: 'Our team reviews your submission and comes back with our best counter-offer — or a comparable alternative.*',
  },
];

// Curated stock images — happy AU customers reviewing bills + savings together
const galleryImages = [
  {
    src: '/svg-assets/happy-couple-bills.jpg',
    alt: 'Happy Australian couple reviewing their household bills at the kitchen table',
  },
  {
    src: '/svg-assets/happy-couple-savings.jpg',
    alt: 'Couple comparing energy savings together on a laptop',
  },
  {
    src: '/svg-assets/happy-family-review.jpg',
    alt: 'Family reviewing a quote together at home',
  },
  {
    src: '/svg-assets/happy-customer-deal.jpg',
    alt: 'Smiling customer happy with a better deal',
  },
];

const SupaValueGuaranteePage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [claimId, setClaimId] = useState('');
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    otp: '',
    file: null,
  });

  // smooth-scroll into claim section on hash
  useEffect(() => {
    if (window.location.hash === '#claim') {
      const el = document.getElementById('claim');
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 200);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const goToClaim = () => {
    const el = document.getElementById('claim');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // ----- Step 1 → Step 2 -----
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error('Please enter your first and last name');
      return;
    }
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!form.mobile.trim() || form.mobile.replace(/\D/g, '').length < 9) {
      toast.error('Please enter a valid Australian mobile number');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/supa-value-guarantee/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: form.mobile,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send OTP');
      setOtpSent(true);
      setCurrentStep(2);
      toast.success(`OTP sent to ${form.mobile}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/supa-value-guarantee/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: form.mobile,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to resend OTP');
      toast.success('A new code has been sent.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ----- Step 2 → Step 3 -----
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!form.otp || form.otp.length !== 6) {
      toast.error('Enter the 6-digit code from SMS');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/supa-value-guarantee/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: form.mobile, otp: form.otp }),
      });
      const data = await res.json();
      if (!res.ok || !data.verified) {
        throw new Error(data.detail || 'Invalid code, try again');
      }
      setOtpVerified(true);
      setCurrentStep(3);
      toast.success('Mobile verified!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ----- Step 3 final submit -----
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File too large — max 10MB');
      return;
    }
    setForm((p) => ({ ...p, file: f }));
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    if (!form.file) {
      toast.error('Please upload your offer/proposal');
      return;
    }
    if (!otpVerified) {
      toast.error('Please verify your mobile first');
      return;
    }

    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append('firstName', form.firstName);
      fd.append('lastName', form.lastName);
      fd.append('email', form.email);
      fd.append('mobile', form.mobile);
      fd.append('otpVerified', 'true');
      fd.append('document', form.file);

      const res = await fetch(`${API_URL}/api/supa-value-guarantee/submit`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setClaimId(data.claim_id || '');
      setSubmitted(true);
      toast.success('Offer received — we will be in touch within 24 hours.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Supa Value Guarantee — Same Products. Better Value. Superior Service."
        description="Send us your written quote — Supa Energy Hub reviews every submission and responds with our best offer or a comparable Tier-1 alternative. CEC-accredited installs across Australia."
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#3D5A47] via-[#34503e] to-[#2d4435]">
          {/* decorative */}
          <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-[#B5D43B]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 lg:pt-32 lg:pb-28 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="text-white">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-5">
                  Same Products.
                  <span className="block text-[#B5D43B]">Better Value.</span>
                  Superior Service.
                </h1>
                <p className="text-base sm:text-lg text-white/85 mb-8 max-w-xl leading-relaxed">
                  Got a quote in hand? Send it across. Our team reviews every
                  submission and comes back with our best offer — and where
                  we can't beat the exact product, we'll offer a comparable
                  Tier-1 alternative that delivers better overall value.*
                </p>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={goToClaim}
                    data-testid="hero-claim-cta"
                    className="bg-[#B5D43B] hover:bg-[#a0c133] text-[#3D5A47] gap-2 px-8 py-6 h-auto text-base font-semibold rounded-full"
                  >
                    Submit My Offer for Review
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      data-testid="hero-contact-cta"
                      className="border-white/30 bg-white/5 text-white hover:bg-white hover:text-[#3D5A47] gap-2 px-8 py-6 h-auto text-base font-semibold rounded-full backdrop-blur"
                    >
                      Talk to a Specialist
                      <Phone className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>

                <p className="text-xs text-white/55 mt-5 max-w-md">
                  *All submissions are reviewed at Supa Energy Hub's
                  discretion. Match, beat or alternate-offer outcome is not
                  guaranteed. See disclaimers below.
                </p>
              </div>

              {/* Hero brand-mark card */}
              <div className="relative flex justify-center lg:justify-end">
                <div
                  className="relative bg-white rounded-3xl shadow-2xl ring-1 ring-white/40 p-8 sm:p-10 lg:p-12 w-full max-w-lg"
                  data-testid="svg-hero-logo-card"
                >
                  <img
                    src="/svg-assets/svg-logo.png"
                    alt="Supa Value Guarantee — Same Products. Better Value. Superior Service."
                    className="block w-full h-auto object-contain"
                    data-testid="svg-hero-logo"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* THREE PROMISES */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                Our Promise To You
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3D5A47] mb-4">
                Why we put it in writing.
              </h2>
              <p className="text-gray-600">
                We work hard, source smart, and pass the savings on. That's
                how we look every customer in the eye and back our word with
                the Supa Value Guarantee.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {promises.map((p, i) => (
                <div
                  key={i}
                  data-testid={`promise-card-${i}`}
                  className="group bg-gray-50 hover:bg-white rounded-2xl p-7 border border-gray-100 hover:border-[#B5D43B]/40 hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#3D5A47] text-[#B5D43B] flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
                    <p.icon className="w-6 h-6" strokeWidth={2.2} />
                  </div>
                  <h3 className="text-xl font-bold text-[#3D5A47] mb-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* HAPPY CUSTOMERS GALLERY */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5">
                <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                  Real Aussie Savings
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#3D5A47] mb-5">
                  Households across Australia trust us with the biggest energy decision of their life.
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  From first-home buyers in Brisbane to retirees on the
                  Mornington Peninsula, our customers tell us the same thing:
                  they got a better price <em>and</em> a better experience —
                  without the call-centre runaround.
                </p>
                <ul className="space-y-3">
                  {[
                    'Every offer is reviewed by our pricing team — humans, not bots.*',
                    "Can't beat the exact product? We'll offer a comparable Tier-1 alternative that delivers better value.*",
                    '10+ years of CEC-accredited installs across NSW, VIC, QLD, SA, WA, ACT, TAS, NT.*',
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                      <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-7 grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img
                    src={galleryImages[0].src}
                    alt={galleryImages[0].alt}
                    loading="lazy"
                    className="rounded-2xl shadow-lg w-full aspect-[4/5] object-cover"
                  />
                  <div className="bg-[#3D5A47] rounded-2xl p-5 text-white shadow-lg">
                    <ShieldCheck className="w-6 h-6 text-[#B5D43B] mb-2" strokeWidth={2.4} />
                    <div className="text-2xl font-bold">CEC*</div>
                    <div className="text-xs text-white/75">
                      Accredited installers backed by 10+ years of Aussie service
                    </div>
                  </div>
                </div>
                <div className="space-y-4 sm:translate-y-8">
                  <div className="bg-[#B5D43B] rounded-2xl p-5 shadow-lg">
                    <CheckCircle className="w-6 h-6 text-[#3D5A47] mb-2" />
                    <div className="text-2xl font-bold text-[#3D5A47]">
                      Every Claim*
                    </div>
                    <div className="text-xs text-[#3D5A47]/85">
                      Reviewed by our pricing team — humans, not bots
                    </div>
                  </div>
                  <img
                    src={galleryImages[1].src}
                    alt={galleryImages[1].alt}
                    loading="lazy"
                    className="rounded-2xl shadow-lg w-full aspect-[4/5] object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                How It Works
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3D5A47] mb-4">
                4 simple steps to a better deal.
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-5">
              {howItWorks.map((s, i) => (
                <div
                  key={i}
                  data-testid={`hiw-step-${i}`}
                  className="relative bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-[#B5D43B]/40 transition-colors"
                >
                  <span className="absolute -top-3 right-5 text-5xl font-extrabold text-[#B5D43B]/30 select-none">
                    {s.n}
                  </span>
                  <div className="w-12 h-12 rounded-xl bg-[#3D5A47] text-[#B5D43B] flex items-center justify-center mb-4">
                    <s.icon className="w-5 h-5" strokeWidth={2.4} />
                  </div>
                  <h3 className="font-bold text-[#3D5A47] mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLAIM FORM */}
        <section id="claim" className="py-20 bg-gradient-to-br from-gray-50 to-white scroll-mt-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                Submit Your Offer
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3D5A47] mb-3">
                Send us your offer. We'll review it and come back with our best deal.*
              </h2>
              <p className="text-gray-600 max-w-xl mx-auto">
                Three quick steps — takes less than 2 minutes. Your details stay
                with our team only. We never share, sell or spam.
              </p>
            </div>

            {/* Step indicator */}
            {!submitted && (
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-10">
                {[1, 2, 3].map((n) => (
                  <React.Fragment key={n}>
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all
                        ${
                          currentStep === n
                            ? 'bg-[#3D5A47] text-[#B5D43B] border-[#3D5A47]'
                            : currentStep > n
                            ? 'bg-[#B5D43B] text-[#3D5A47] border-[#B5D43B]'
                            : 'bg-white text-gray-400 border-gray-300'
                        }`}
                      data-testid={`step-indicator-${n}`}
                    >
                      {currentStep > n ? <CheckCircle className="w-4 h-4" /> : n}
                    </div>
                    {n < 3 && (
                      <div
                        className={`w-12 h-0.5 ${
                          currentStep > n ? 'bg-[#B5D43B]' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}

            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-7 sm:p-10">
              {submitted ? (
                <div className="text-center py-6" data-testid="svg-success">
                  <div className="w-16 h-16 rounded-full bg-[#B5D43B] flex items-center justify-center mx-auto mb-5">
                    <CheckCircle className="w-9 h-9 text-[#3D5A47]" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#3D5A47] mb-3">
                    Offer received — under review.
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-5">
                    Our pricing team is reviewing your submission. We'll be in
                    touch on <strong>{form.email}</strong> or by phone on{' '}
                    <strong>{form.mobile}</strong> with our offer or a
                    comparable alternative.
                  </p>
                  {claimId && (
                    <p className="text-xs text-gray-400 font-mono">
                      Reference: {claimId.slice(0, 8).toUpperCase()}
                    </p>
                  )}
                  <Link to="/" className="inline-block mt-6">
                    <Button
                      variant="outline"
                      data-testid="svg-success-back-home"
                      className="border-[#3D5A47] text-[#3D5A47] hover:bg-[#3D5A47] hover:text-white rounded-full"
                    >
                      Back to Home
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  {/* STEP 1 — contact */}
                  {currentStep === 1 && (
                    <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="svg-step-1-form">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="firstName">First Name *</Label>
                          <Input
                            id="firstName"
                            name="firstName"
                            value={form.firstName}
                            onChange={handleChange}
                            placeholder="Jane"
                            data-testid="svg-first-name"
                            className="mt-1.5"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="lastName">Last Name *</Label>
                          <Input
                            id="lastName"
                            name="lastName"
                            value={form.lastName}
                            onChange={handleChange}
                            placeholder="Smith"
                            data-testid="svg-last-name"
                            className="mt-1.5"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <div className="relative mt-1.5">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="jane@example.com"
                            data-testid="svg-email"
                            className="pl-10"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="mobile">Mobile Number *</Label>
                        <div className="relative mt-1.5">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            id="mobile"
                            name="mobile"
                            type="tel"
                            value={form.mobile}
                            onChange={handleChange}
                            placeholder="0412 345 678"
                            data-testid="svg-mobile"
                            className="pl-10"
                            required
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5">
                          We'll send you a 6-digit code by SMS to verify ownership.
                        </p>
                      </div>

                      <Button
                        type="submit"
                        disabled={isLoading}
                        data-testid="svg-step-1-submit"
                        className="w-full bg-[#3D5A47] hover:bg-[#2d4435] text-white gap-2 py-6 h-auto text-base font-semibold rounded-xl"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" /> Sending code…
                          </>
                        ) : (
                          <>
                            Continue <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </Button>
                    </form>
                  )}

                  {/* STEP 2 — OTP */}
                  {currentStep === 2 && (
                    <form onSubmit={handleVerifyOtp} className="space-y-5" data-testid="svg-step-2-form">
                      <div className="text-center mb-2">
                        <div className="w-14 h-14 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-4">
                          <Lock className="w-6 h-6 text-[#3D5A47]" strokeWidth={2.4} />
                        </div>
                        <h3 className="text-xl font-bold text-[#3D5A47] mb-1.5">
                          Verify your mobile
                        </h3>
                        <p className="text-sm text-gray-600">
                          Enter the 6-digit code we sent to{' '}
                          <strong className="text-[#3D5A47]">{form.mobile}</strong>
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="otp" className="sr-only">
                          OTP
                        </Label>
                        <Input
                          id="otp"
                          name="otp"
                          inputMode="numeric"
                          maxLength={6}
                          value={form.otp}
                          onChange={handleChange}
                          placeholder="••••••"
                          data-testid="svg-otp-input"
                          className="text-center text-2xl font-mono tracking-[0.6em] py-6 h-auto"
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isLoading}
                        data-testid="svg-verify-otp-btn"
                        className="w-full bg-[#3D5A47] hover:bg-[#2d4435] text-white gap-2 py-6 h-auto text-base font-semibold rounded-xl"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
                          </>
                        ) : (
                          <>
                            Verify &amp; Continue <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </Button>

                      <div className="flex items-center justify-between text-sm">
                        <button
                          type="button"
                          onClick={() => setCurrentStep(1)}
                          data-testid="svg-back-step-1"
                          className="text-gray-500 hover:text-[#3D5A47] underline-offset-2 hover:underline"
                        >
                          ← Edit details
                        </button>
                        <button
                          type="button"
                          onClick={resendOtp}
                          disabled={isLoading}
                          data-testid="svg-resend-otp"
                          className="text-[#3D5A47] font-semibold hover:underline disabled:opacity-50"
                        >
                          Resend code
                        </button>
                      </div>
                    </form>
                  )}

                  {/* STEP 3 — upload */}
                  {currentStep === 3 && (
                    <form onSubmit={handleFinalSubmit} className="space-y-5" data-testid="svg-step-3-form">
                      <div className="text-center mb-2">
                        <div className="w-14 h-14 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-4">
                          <Upload className="w-6 h-6 text-[#3D5A47]" strokeWidth={2.4} />
                        </div>
                        <h3 className="text-xl font-bold text-[#3D5A47] mb-1.5">
                          Upload your offer
                        </h3>
                        <p className="text-sm text-gray-600 max-w-md mx-auto">
                          Attach the quote, proposal, photo or PDF of the deal
                          you have in hand. PDF, JPG, PNG or DOC — max 10MB.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="svg-file-pick-btn"
                        className="w-full rounded-2xl border-2 border-dashed border-gray-300 hover:border-[#B5D43B] hover:bg-[#B5D43B]/5 p-8 transition-colors text-center"
                      >
                        {form.file ? (
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 rounded-xl bg-[#B5D43B] flex items-center justify-center">
                              <FileText className="w-6 h-6 text-[#3D5A47]" />
                            </div>
                            <div className="font-semibold text-[#3D5A47]">{form.file.name}</div>
                            <div className="text-xs text-gray-500">
                              {(form.file.size / 1024).toFixed(0)} KB · click to replace
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="w-8 h-8 text-gray-400" />
                            <div className="font-semibold text-[#3D5A47]">
                              Click to upload
                            </div>
                            <div className="text-xs text-gray-500">
                              PDF, JPG, PNG, DOC — up to 10MB
                            </div>
                          </div>
                        )}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        className="hidden"
                        onChange={handleFileChange}
                        data-testid="svg-file-input"
                      />

                      <Button
                        type="submit"
                        disabled={isLoading || !form.file}
                        data-testid="svg-final-submit"
                        className="w-full bg-[#3D5A47] hover:bg-[#2d4435] text-white gap-2 py-6 h-auto text-base font-semibold rounded-xl"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                          </>
                        ) : (
                          <>
                            Submit My Offer <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-center text-gray-500">
                        By submitting, you agree to our{' '}
                        <Link to="/privacy" className="underline">
                          Privacy Policy
                        </Link>{' '}
                        and the Supa Value Guarantee terms below.
                      </p>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* THIRD CALL TO ACTION + IMAGE */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <img
                src={galleryImages[2].src}
                alt={galleryImages[2].alt}
                loading="lazy"
                className="rounded-3xl shadow-xl w-full aspect-[4/3] object-cover"
              />
              <div>
                <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                  Not Just A Slogan
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#3D5A47] mb-5">
                  Every eligible claim is reviewed by our pricing team.*
                </h2>
                <p className="text-gray-600 mb-5 leading-relaxed">
                  Where we can beat the price on the exact product you've been
                  quoted, we will. Where we can't, we'll offer a comparable
                  Tier-1 alternative that delivers better overall value. The
                  final decision on whether to match, beat or counter-offer
                  always rests with Supa Energy Hub — and we tell you straight
                  up either way. No pressure, no spin.*
                </p>
                <Button
                  onClick={goToClaim}
                  data-testid="bottom-cta"
                  className="bg-[#3D5A47] hover:bg-[#2d4435] text-white gap-2 px-8 py-6 h-auto text-base font-semibold rounded-full"
                >
                  Submit My Offer for Review
                  <Zap className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* DISCLAIMERS */}
        <section
          id="disclaimers"
          className="py-14 bg-white border-t border-gray-100"
          data-testid="svg-disclaimers"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-[#3D5A47]" strokeWidth={2.4} />
                <h2 className="text-lg sm:text-xl font-bold text-[#3D5A47]">
                  Supa Value Guarantee — Terms &amp; Important Information
                  <span className="text-[#B5D43B] ml-1">*</span>
                </h2>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed mb-4">
                All claims, savings figures, response references and
                "review/beat/comparable-alternative" statements marked with an
                asterisk{' '}
                <span className="text-[#3D5A47] font-semibold">(*)</span> on
                this page are indicative only and subject to the terms set out
                below. Nothing on this page constitutes a binding offer,
                contract, energy advice, financial advice, or a guarantee of
                any specific outcome.
              </p>
              <ol className="list-decimal pl-5 space-y-2 text-xs text-gray-600 leading-relaxed">
                <li>
                  <strong>Sole discretion.</strong> Whether Supa Energy Hub
                  reviews, matches, beats, declines, or offers a comparable
                  alternative to any submitted offer is determined at Supa
                  Energy Hub's sole and absolute discretion. Submission of a
                  claim does not create any obligation on Supa Energy Hub to
                  make a counter-offer or to enter into a contract with you.
                  We may decline any claim for any reason, including reasons
                  not stated on this page.
                </li>
                <li>
                  <strong>What "Supa Value Guarantee" means.</strong> The Supa
                  Value Guarantee is our undertaking to <em>review</em> every
                  eligible written quote you submit and respond with our best
                  available offer. Where we are able to beat the price on the
                  exact product or brand you have been quoted, we will. Where
                  we are not able to do so, we may, at our discretion, offer a
                  comparable Tier-1 product or system that, in our reasonable
                  view, delivers equivalent or better overall value. We do
                  <em>not</em> guarantee that the alternative will be cheaper
                  than the offer you submitted.
                </li>
                <li>
                  <strong>Eligibility.</strong> The Supa Value Guarantee is
                  available to private Australian residential customers,
                  18 years or older, located in Supa Energy Hub's service
                  area at the time of claim. Commercial, wholesale,
                  bulk-purchase, government tender and employee/staff
                  pricing offers are excluded.
                </li>
                <li>
                  <strong>Qualifying offers.</strong> We will only assess
                  written, dated and itemised quotes from CEC-accredited
                  Australian installers that include identifiable installer
                  details, product brand, capacity, scope of works, location
                  and warranty terms. We do not assess verbal quotes,
                  expired quotes, screenshots without installer details,
                  social-media advertisements, marketplace lead-gen prices,
                  manufacturer direct-to-public pricing, group-buying or
                  community-bulk-buy programs, pre-payment-only deals, or
                  "today only" / time-pressured promotions.
                </li>
                <li>
                  <strong>Beat amount &amp; format.</strong> Where we elect
                  to match or beat, the amount by which we do so is at our
                  reasonable discretion and depends on the product mix, our
                  supply costs at the time, applicable rebates (STCs,
                  VEECs, ESCs/PDRS, REPS, EEIS, Solar Vic, etc.), and
                  installation complexity. We do not publish or guarantee
                  any specific average saving figure on this page —
                  individual outcomes vary, and some claims may not be
                  beaten at all.
                </li>
                <li>
                  <strong>Review timeframe.</strong> We will get back to you
                  as soon as practicable after your submission. Specific
                  turnaround times are not guaranteed and may vary
                  depending on the volume of claims, the complexity of your
                  submission, and the need to source supplier pricing.
                </li>
                <li>
                  <strong>Validity &amp; reservation of rights.</strong> Any
                  counter-offer or alternative offer we provide is valid
                  for 14 days from the date of issue unless we state
                  otherwise. We reserve the right, at our discretion, to
                  (a) decline to match, beat or counter-offer; (b) request
                  additional information; (c) refuse fraudulent, altered or
                  unverifiable quotes; and (d) withdraw or amend the Supa
                  Value Guarantee at any time without notice. Withdrawal
                  does not affect counter-offers already issued and
                  accepted in writing.
                </li>
                <li>
                  <strong>OTP &amp; identity verification.</strong> By
                  entering your mobile number you consent to Supa Energy
                  Hub sending you a one-time SMS verification code through
                  our SMS provider (Twilio). Standard carrier SMS charges
                  may apply at your carrier's discretion. We do not
                  disclose your verification code to any third party.
                </li>
                <li>
                  <strong>File uploads.</strong> Files you upload may
                  contain personal or commercial information. By
                  uploading, you confirm you have the right to share the
                  document and consent to Supa Energy Hub reviewing it for
                  the sole purpose of assessing your claim and preparing a
                  response. Files are stored securely and handled in
                  accordance with our{' '}
                  <Link to="/privacy" className="underline text-[#3D5A47]">
                    Privacy Policy
                  </Link>
                  . Maximum file size 10MB; accepted formats: PDF, JPG,
                  PNG, DOC/DOCX.
                </li>
                <li>
                  <strong>Privacy.</strong> Personal information collected
                  (name, email, mobile, uploaded documents) is used only
                  to assess your claim, contact you about your offer, and
                  comply with our legal obligations. We do not sell or
                  rent your information. See our{' '}
                  <Link to="/privacy" className="underline text-[#3D5A47]">
                    Privacy Policy
                  </Link>{' '}
                  for full details, including how to access, correct or
                  delete your data.
                </li>
                <li>
                  <strong>Testimonial and aggregate claims.</strong>{' '}
                  Statements such as "Reviewed by real humans" or "10+ years
                  of CEC-accredited installs" are based on aggregate Supa
                  Energy Hub records and customer feedback over the past
                  decade and are accurate to the best of our knowledge at
                  the time of publishing.
                </li>
                <li>
                  <strong>Images.</strong> Photographs on this page
                  include stock photography and AI-generated lifestyle
                  imagery intended to illustrate happy customers and
                  household scenarios (such as reviewing bills or energy
                  quotes at home). They are not photographs of specific
                  Supa Energy Hub customers, employees, or installations
                  unless explicitly captioned as such. Any third-party
                  brand names, logos or document layouts that may appear
                  incidentally within generated imagery are used purely
                  for illustrative purposes and do not imply endorsement,
                  affiliation or sponsorship.
                </li>
                <li>
                  <strong>Logo &amp; trademarks.</strong> "Supa Value
                  Guarantee" and the Supa Value Guarantee shield-and-tag
                  logo are trademarks of Supa Energy Hub. All
                  other trademarks, product names and logos referenced on
                  this site are the property of their respective owners
                  and are used for identification or comparison purposes
                  only.
                </li>
                <li>
                  <strong>No professional advice.</strong> Information on
                  this page is general in nature and does not take into
                  account your individual objectives, financial situation,
                  energy usage, property characteristics or eligibility
                  for any government rebate or incentive. Please obtain
                  independent advice as appropriate.
                </li>
              </ol>
              <p className="text-xs text-gray-500 leading-relaxed mt-5 border-t border-gray-200 pt-4">
                Supa Energy Hub complies with the Australian
                Consumer Law (Schedule 2 of the Competition and Consumer
                Act 2010 (Cth)) and the Australian Privacy Principles
                (Privacy Act 1988 (Cth)). Nothing on this page is intended
                to mislead or deceive, or to exclude, restrict or modify
                any consumer guarantee or right that cannot lawfully be
                excluded.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SupaValueGuaranteePage;
